/**
 * Product Q&A constants (epic PI-SPEC-SW-E310, ER-1667 Package F, contract
 * ask 1).
 *
 * Single source of truth for the limits, verdicts, source kinds, run phases
 * and status states that the SpecTree web "Ask about the product" panel, the
 * internal Product Q&A plugin skill and the SpecTree Toolbox ask engine share.
 * Import these; never re-type the numbers. The screenshot limits are Help's
 * own constants under another name, so the two panels can never drift.
 * Browser-safe: no runtime dependencies (the zod schemas that use these live
 * in `types.ts` and `api.ts`).
 */
import { HELP_IMAGE_BYTES_MAX, HELP_IMAGE_MIME_TYPES, HELP_IMAGE_NAME_MAX, HELP_IMAGES_MAX, } from "../help/constants.js";
/**
 * The marker the internal skill prints at the start of its one result line:
 * `SPECTREE_ASK_TURN {json}`. The JSON parses with `askTurnMarkerSchema` and
 * is narrowed with `normalizeAskTurnMarker`. The engine strips the line from
 * the streamed answer text (`stripAskTurnMarker`), so it never reaches the
 * panel.
 */
export const ASK_TURN_MARKER = "SPECTREE_ASK_TURN";
/** Version of the `turn.json` file the ask engine writes for the skill (`schemaVersion`). */
export const ASK_TURN_INPUT_SCHEMA_VERSION = 1;
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/** Most screenshots one question may carry. Help's limit: never a separate number. */
export const ASK_IMAGES_MAX = HELP_IMAGES_MAX;
/** Largest screenshot, in decoded bytes. Help's limit: never a separate number. */
export const ASK_IMAGE_BYTES_MAX = HELP_IMAGE_BYTES_MAX;
/** Image types Product Q&A accepts for a screenshot. Help's list. */
export const ASK_IMAGE_MIME_TYPES = HELP_IMAGE_MIME_TYPES;
/** Longest screenshot file name. Help's limit. */
export const ASK_IMAGE_NAME_MAX = HELP_IMAGE_NAME_MAX;
/** Longest question a person may type. */
export const ASK_QUESTION_MAX = 4000;
/** Most earlier messages the engine hands the skill in `turn.json`. */
export const ASK_TURN_MESSAGES_MAX = 12;
/** Longest single message text in `turn.json` or the conversation. */
export const ASK_MESSAGE_MAX = 20_000;
/** Longest answer the engine reports in `turn-finished`. */
export const ASK_ANSWER_MAX = 20_000;
/** Most "Where in the code" references an answer keeps (extra ones are dropped, not refused). */
export const ASK_CODE_REFS_MAX = 12;
/** Most sources an answer keeps (extra ones are dropped, not refused). */
export const ASK_SOURCES_MAX = 8;
/** Longest repository-relative path in a code reference. */
export const ASK_CODE_REF_PATH_MAX = 1024;
/** Longest plain note on a code reference ("Checks the session has not expired"). */
export const ASK_CODE_REF_NOTE_MAX = 500;
/** Longest source title. */
export const ASK_SOURCE_TITLE_MAX = 200;
/** Longest source reference (a repo doc path, `AB#123` or a FogBugz case number). */
export const ASK_SOURCE_REF_MAX = 300;
/**
 * How long an untouched conversation lives on the computer before the engine
 * sweeps it (Resolved Decision 10d, the same as Help): 24 hours. Nothing is
 * stored on the server.
 */
export const ASK_CONVERSATION_TTL_MS = 24 * 60 * 60_000;
// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------
/**
 * What a question is about:
 * - `ado-project`: the mapped repositories of one ADO project;
 * - `fogbugz-project`: a FogBugz project (and optional area), answered from
 *   the repositories of the ADO project it is linked to (FogBugz ↔ ADO link,
 *   ER-1664). Part of ask 1 from the start so wiring it never moves the
 *   contract number.
 */
export const ASK_SCOPE_KINDS = ["ado-project", "fogbugz-project"];
// ---------------------------------------------------------------------------
// Verdicts and sources
// ---------------------------------------------------------------------------
/**
 * How the skill judged its own answer (Resolved Decision 10b; epic decision
 * on the `answered-from-code` / `code-only` split):
 * - `answered-from-code`: a how-does-it-work-now question; the code answers
 *   it and intent does not matter (no warning);
 * - `spec-found`: an is-this-supposed-to-happen question with a written spec
 *   found (repo doc, ADO work item or FogBugz case); `specAgrees` says whether
 *   the code matches it;
 * - `code-only`: an is-this-supposed-to-happen question with no written spec
 *   found; the panel warns that nobody wrote the intended behaviour down;
 * - `not-sure`: the code does not show it clearly and the answer says so;
 * - `off-topic`: the question is not about this product.
 */
export const ASK_VERDICTS = ["answered-from-code", "spec-found", "code-only", "not-sure", "off-topic"];
/** The plain label of each verdict. `spec-found` reads by `specAgrees`: see {@link askVerdictLabel}. */
export const ASK_VERDICT_LABELS = {
    "answered-from-code": "From the code",
    "spec-found": "Code and spec agree",
    "code-only": "Code only - no written spec found",
    "not-sure": "Not sure",
    "off-topic": "Not about this product",
};
/**
 * Whether the code matches the written spec a `spec-found` answer found.
 * `no` is the bug signal: the panel says the two disagree and makes the
 * "Start FogBugz Formulator" hand-off more prominent.
 */
export const ASK_SPEC_AGREEMENTS = ["yes", "no", "partly"];
/** The plain label of a `spec-found` answer for each `specAgrees` value. */
export const ASK_SPEC_AGREEMENT_LABELS = {
    yes: "Code and spec agree",
    no: "Code and spec disagree - this may be a bug",
    partly: "Code and spec partly agree",
};
/**
 * The label the panel shows for a verdict. A `spec-found` answer reads by
 * `specAgrees`; without one it reads as agreeing.
 */
export function askVerdictLabel(verdict, specAgrees) {
    if (verdict === "spec-found" && specAgrees !== undefined)
        return ASK_SPEC_AGREEMENT_LABELS[specAgrees];
    return ASK_VERDICT_LABELS[verdict];
}
/**
 * Where an answer's source came from: the product `code`, a doc in the
 * repository (`repo-doc`), an ADO work item (`ado-work-item`, opened by id
 * only) or a FogBugz case in the linked project (`fogbugz-case`).
 */
export const ASK_SOURCE_KINDS = ["code", "repo-doc", "ado-work-item", "fogbugz-case"];
/** The source kinds that count as a written spec: a `spec-found` answer needs one. */
export const ASK_SPEC_SOURCE_KINDS = ["repo-doc", "ado-work-item", "fogbugz-case"];
/** True when `kind` is a written-spec source kind. */
export function isAskSpecSourceKind(kind) {
    return typeof kind === "string" && ASK_SPEC_SOURCE_KINDS.includes(kind);
}
/**
 * Where an off-topic answer may send the person instead: `help` (the question
 * is about SpecTree Toolbox itself, so Help answers it).
 */
export const ASK_REDIRECTS = ["help"];
/**
 * True when `value` is a path relative to a repository root, as a "Where in
 * the code" reference must be: not empty, no leading `/`, no backslash, no
 * drive letter (`C:`), no `..` or `.` segment, no empty segment, no NUL or
 * other control character.
 */
export function isAskCodeRefPath(value) {
    if (typeof value !== "string" || value.length === 0 || value.length > ASK_CODE_REF_PATH_MAX)
        return false;
    if (/\p{Cc}/u.test(value))
        return false;
    if (value.startsWith("/") || value.includes("\\") || /^[A-Za-z]:/.test(value))
        return false;
    return value.split("/").every((part) => part !== "" && part !== ".." && part !== ".");
}
// ---------------------------------------------------------------------------
// Conversation and runs
// ---------------------------------------------------------------------------
/** Who wrote a message in a Product Q&A conversation. */
export const ASK_MESSAGE_ROLES = ["person", "ask"];
/** How an ask turn ended, as `turn-finished` reports it. */
export const ASK_TURN_OUTCOMES = ["answered", "failed", "stopped"];
/**
 * Phases of one ask turn, in order, as the engine reports them in
 * `turn-progress` events. Pages show {@link ASK_RUN_PHASE_LABELS}, never the id.
 */
export const ASK_RUN_PHASES = ["starting", "looking-at-screen", "reading-code", "checking-specs", "writing"];
/** The plain phrase the panel shows for each phase. */
export const ASK_RUN_PHASE_LABELS = {
    starting: "Getting started",
    "looking-at-screen": "Looking at your screenshot",
    "reading-code": "Reading the product code",
    "checking-specs": "Checking for a written spec",
    writing: "Writing the answer",
};
/**
 * Readiness of the ask engine, as `ask.getStatus()` and `status-changed`
 * report it. Every state other than `ready` shows a plain message and the way
 * to Setup instead of a chat.
 */
export const ASK_STATUS_STATES = ["ready", "claude-missing", "signed-out", "usage-limit", "plugin-missing"];
// ---------------------------------------------------------------------------
// Answer depth (Resolved Decision 10)
// ---------------------------------------------------------------------------
/**
 * Whether the "Where in the code" fold starts open for someone with `roles`
 * on the product's project: open for Admins and Developers, closed for
 * everyone else (Project Managers, Product Owners and QA Testers get the plain
 * answer first; the file and line detail is one click away).
 */
export function askCodeFoldOpenByDefault(roles) {
    return roles.includes("admin") || roles.includes("developer");
}
//# sourceMappingURL=constants.js.map